import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Plus, Edit, Upload, Calculator, Percent, Euro, Save, AlertTriangle, Info, Eye, CheckCircle, X} from 'lucide-react';
export default function ModalsParamGroup({
  dialogOpen, setDialogOpen,
  editMode, setEditMode,
  formData, setFormData: setFormData,
  handleInputChange,
  handleSubmit,
  importDialogOpen, setImportDialogOpen,
  importData, setImportData,
  confirmImport,
  fileInputRef: _fileInputRef,
  handleFileImport: _handleFileImport,
  previewDialogOpen, setPreviewDialogOpen,
  previewParams,
  customParamDialogOpen, setCustomParamDialogOpen,
  editingCustomParam,
  customParamForm, setCustomParamForm,
  handleSaveCustomParam,
  toggleApplicableA,
  restrictionDialogOpen, setRestrictionDialogOpen,
  editingRestriction,
  restrictionForm, setRestrictionForm,
  handleSaveRestriction,
  territoiresDisponibles,
  ajustementDialogOpen, setAjustementDialogOpen,
  editingAjustement,
  ajustementForm, setAjustementForm,
  handleCreateAjustement,
  handleUpdateAjustement,
  handleEdit}) {
  return (
    <>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editMode ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                {editMode ? 'Modifier les Paramètres' : 'Créer de Nouveaux Paramètres'}
              </DialogTitle>
              <DialogDescription>
                {editMode 
                  ? 'Modifiez les paramètres existants. Les modifications seront sauvegardées avec horodatage.'
                  : 'Créer un nouveau jeu de paramètres désactivera automatiquement les paramètres actuellement actifs.'
                }
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Coefficients */}
              <div className="border border-slate-200 rounded-xl p-4">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-blue-600" />
                  Coefficients RCD Solidaire
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="coef_gros_oeuvre">Gros Œuvre</Label>
                    <Input
                      id="coef_gros_oeuvre"
                      name="coef_gros_oeuvre"
                      type="number"
                      step="0.000001"
                      value={formData.coef_gros_oeuvre}
                      onChange={handleInputChange}
                      className="font-mono"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="coef_second_oeuvre">Second Œuvre</Label>
                    <Input
                      id="coef_second_oeuvre"
                      name="coef_second_oeuvre"
                      type="number"
                      step="0.000001"
                      value={formData.coef_second_oeuvre}
                      onChange={handleInputChange}
                      className="font-mono"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="coef_intellectuels">Professionnels Intellectuels</Label>
                    <Input
                      id="coef_intellectuels"
                      name="coef_intellectuels"
                      type="number"
                      step="0.000001"
                      value={formData.coef_intellectuels}
                      onChange={handleInputChange}
                      className="font-mono"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="coef_travaux_publics">Travaux Publics</Label>
                    <Input
                      id="coef_travaux_publics"
                      name="coef_travaux_publics"
                      type="number"
                      step="0.000001"
                      value={formData.coef_travaux_publics}
                      onChange={handleInputChange}
                      className="font-mono"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Remises */}
              <div className="border border-slate-200 rounded-xl p-4">
                <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                  <Percent className="h-5 w-5 text-green-600" />
                  Remises (gérées par Super Admin)
                </h3>
                <Alert className="mb-4 bg-amber-50 border-amber-200">
                  <Info className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    Les remises sont exprimées en décimal (0.0 à 1.0). Par exemple, 0.175 = 17.5% de remise.
                  </AlertDescription>
                </Alert>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="remise_gros_oeuvre">Remise Gros Œuvre</Label>
                    <Input
                      id="remise_gros_oeuvre"
                      name="remise_gros_oeuvre"
                      type="number"
                      step="0.001"
                      min="0"
                      max="1"
                      value={formData.remise_gros_oeuvre}
                      onChange={handleInputChange}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="remise_second_oeuvre">Remise Second Œuvre</Label>
                    <Input
                      id="remise_second_oeuvre"
                      name="remise_second_oeuvre"
                      type="number"
                      step="0.001"
                      min="0"
                      max="1"
                      value={formData.remise_second_oeuvre}
                      onChange={handleInputChange}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="remise_intellectuels">Remise PIB</Label>
                    <Input
                      id="remise_intellectuels"
                      name="remise_intellectuels"
                      type="number"
                      step="0.001"
                      min="0"
                      max="1"
                      value={formData.remise_intellectuels}
                      onChange={handleInputChange}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="remise_travaux_publics">Remise Travaux Publics</Label>
                    <Input
                      id="remise_travaux_publics"
                      name="remise_travaux_publics"
                      type="number"
                      step="0.001"
                      min="0"
                      max="1"
                      value={formData.remise_travaux_publics}
                      onChange={handleInputChange}
                      className="font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Seuil CA */}
              <div className="border border-slate-200 rounded-xl p-4">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Euro className="h-5 w-5 text-amber-600" />
                  Seuil de Chiffre d&apos;Affaires
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="seuil_ca">Montant du seuil (€)</Label>
                    <Input
                      id="seuil_ca"
                      name="seuil_ca"
                      type="number"
                      step="1000"
                      value={formData.seuil_ca}
                      onChange={handleInputChange}
                      className="font-mono"
                      required
                    />
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="seuil_ca_actif"
                      name="seuil_ca_actif"
                      checked={formData.seuil_ca_actif}
                      onChange={handleInputChange}
                      className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Label htmlFor="seuil_ca_actif" className="font-normal cursor-pointer">
                      Activer la restriction sur le seuil CA (désactivé par défaut)
                    </Label>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex gap-2 pt-4">
                <Button 
                  type="button" 
                  onClick={() => {
                    setDialogOpen(false);
                    setEditMode(false);
                  }} 
                  variant="outline"
                >
                  <X className="h-4 w-4 mr-2" />
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editMode ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Import Confirmation Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-blue-600" />
                Confirmer l&apos;import
              </DialogTitle>
              <DialogDescription>
                Vérifiez les données avant de confirmer l&apos;import.
              </DialogDescription>
            </DialogHeader>

            {importData && (
              <div className="space-y-4">
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    {importData.parametres_actifs 
                      ? 'Import depuis fichier JSON - Paramètres actifs détectés'
                      : `Import depuis fichier CSV - ${importData.parametres?.length || 0} entrée(s) détectée(s)`
                    }
                  </AlertDescription>
                </Alert>

                <div className="bg-slate-50 rounded-lg p-4 text-sm font-mono max-h-60 overflow-y-auto">
                  <pre className="whitespace-pre-wrap">
                    {JSON.stringify(
                      importData.parametres_actifs || importData.parametres?.[0], 
                      null, 
                      2
                    )}
                  </pre>
                </div>

                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    L&apos;import créera de nouveaux paramètres qui deviendront actifs. Les paramètres actuels seront désactivés.
                  </AlertDescription>
                </Alert>
              </div>
            )}

            <DialogFooter className="flex gap-2">
              <Button 
                onClick={() => {
                  setImportDialogOpen(false);
                  setImportData(null);
                }} 
                variant="outline"
              >
                Annuler
              </Button>
              <Button 
                onClick={confirmImport}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirmer l&apos;import
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-600" />
                Aperçu des Paramètres
              </DialogTitle>
              <DialogDescription>
                {previewParams && `Créé le ${new Date(previewParams.created_at).toLocaleString('fr-FR')}`}
              </DialogDescription>
            </DialogHeader>

            {previewParams && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="border-blue-200 bg-blue-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-blue-800">Coefficients</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 font-mono text-sm">
                      <div className="flex justify-between">
                        <span>Gros Œuvre:</span>
                        <span className="font-bold">{previewParams.coef_gros_oeuvre}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Second Œuvre:</span>
                        <span className="font-bold">{previewParams.coef_second_oeuvre}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>PIB:</span>
                        <span className="font-bold">{previewParams.coef_intellectuels}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Travaux Publics:</span>
                        <span className="font-bold">{previewParams.coef_travaux_publics}</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-green-200 bg-green-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-green-800">Remises</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 font-mono text-sm">
                      <div className="flex justify-between">
                        <span>Gros Œuvre:</span>
                        <span className="font-bold">{(previewParams.remise_gros_oeuvre * 100).toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Second Œuvre:</span>
                        <span className="font-bold">{(previewParams.remise_second_oeuvre * 100).toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>PIB:</span>
                        <span className="font-bold">{(previewParams.remise_intellectuels * 100).toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Travaux Publics:</span>
                        <span className="font-bold">{(previewParams.remise_travaux_publics * 100).toFixed(1)}%</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-amber-800">Seuil CA</CardTitle>
                  </CardHeader>
                  <CardContent className="flex items-center justify-between">
                    <span className="font-mono text-xl font-bold text-amber-700">
                      {previewParams.seuil_ca.toLocaleString('fr-FR')} €
                    </span>
                    <Badge className={previewParams.seuil_ca_actif ? 'bg-orange-700 text-white' : 'bg-slate-700 text-white'}>
                      {previewParams.seuil_ca_actif ? 'Restriction Active' : 'Restriction Inactive'}
                    </Badge>
                  </CardContent>
                </Card>

                <div className="flex gap-2 text-xs text-slate-500">
                  <Badge variant="outline">ID: {previewParams.id?.substring(0, 16)}...</Badge>
                  {previewParams.is_active && <Badge className="bg-green-700 text-white font-semibold">Actif</Badge>}
                  {previewParams.is_archived && <Badge className="bg-slate-700 text-white font-semibold">Archivé</Badge>}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => setPreviewDialogOpen(false)} variant="outline">
                Fermer
              </Button>
              {previewParams && !previewParams.is_archived && (
                <Button onClick={() => {
                  setPreviewDialogOpen(false);
                  handleEdit(previewParams);
                }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Modifier
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Paramètre Personnalisé */}
    </>
  );
}
